import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded';
import TuneIcon from '@mui/icons-material/Tune';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import { useSearchParams } from 'react-router-dom';
import AssetCard from '../../dashboard/components/AssetCard';
import AssetFilters from '../components/AssetFilters';
import Pagination from '../components/Pagination';
import Footer from '../../../shared/components/Footer';
import { getAssetsByZone } from '../data/assetsMockData';
import { useZone } from '../../../shared/context/ZoneContext';

const ITEMS_PER_PAGE = 8;

const AssetsPage = () => {
  const { selectedZone } = useZone();
  const [searchParams] = useSearchParams();
  const zoneAssets = getAssetsByZone(selectedZone);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState(() => {
    const criticality = searchParams.get('criticality') || 'All Criticalities';
    return {
      criticality,
      supervisor: 'All Supervisors',
      technician: 'All Technicians',
      confidence: 65,
      rulPercent: 65,
    };
  });

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const filteredAssets = useMemo(() => {
    return zoneAssets.filter((asset) => {
      if (searchQuery && !asset.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      if (filters.criticality !== 'All Criticalities' && asset.riskLevel !== filters.criticality) {
        return false;
      }
      if (filters.supervisor !== 'All Supervisors' && asset.supervisor !== filters.supervisor) {
        return false;
      }
      if (filters.technician !== 'All Technicians' && asset.technician !== filters.technician) {
        return false;
      }
      if (asset.confidence < filters.confidence) {
        return false;
      }
      if (asset.rulPercentage < filters.rulPercent) {
        return false;
      }
      return true;
    });
  }, [searchQuery, filters, zoneAssets]);

  const totalPages = Math.ceil(filteredAssets.length / ITEMS_PER_PAGE);
  const paginatedAssets = filteredAssets.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3, flexGrow: 1 }}>
        {/* Header Row */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InventoryOutlinedIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
            <Typography variant="h4" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
              Assets
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* Search */}
            <TextField
              placeholder="Search"
              size="small"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 240,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fff',
                  '& fieldset': { borderColor: '#E5E7EB' },
                },
                '& .MuiOutlinedInput-input': { py: 1, fontSize: '0.875rem' },
              }}
            />

            {/* View Toggle */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, newMode) => newMode && setViewMode(newMode)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px !important',
                  px: 1.25,
                  py: 0.75,
                  '&.Mui-selected': {
                    bgcolor: '#1B2A3D',
                    color: '#fff',
                    '&:hover': { bgcolor: '#2D4156' },
                  },
                },
              }}
            >
              <ToggleButton value="grid">
                <GridViewRoundedIcon sx={{ fontSize: 20 }} />
              </ToggleButton>
              <ToggleButton value="list">
                <ViewListRoundedIcon sx={{ fontSize: 20 }} />
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Filters Button */}
            <Button
              variant="contained"
              startIcon={<TuneIcon sx={{ fontSize: 18 }} />}
              onClick={() => setShowFilters((prev) => !prev)}
              sx={{
                bgcolor: '#1B2A3D',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.8125rem',
                px: 2.5,
                py: 0.85,
                borderRadius: 2,
                '&:hover': { bgcolor: '#2D4156' },
              }}
            >
              Filters
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        {showFilters && (
          <Box sx={{ mb: 3 }}>
            <AssetFilters filters={filters} onFilterChange={handleFilterChange} />
          </Box>
        )}

        {/* Asset Cards Grid */}
        <Grid container spacing={2.5}>
          {paginatedAssets.map((asset) => (
            <Grid size={{ xs: 12, md: viewMode === 'grid' ? 6 : 12 }} key={asset.id}>
              <AssetCard asset={asset} />
            </Grid>
          ))}
        </Grid>

        {/* Empty State */}
        {filteredAssets.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
              No assets found
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Try adjusting your search or filter criteria
            </Typography>
          </Box>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </Box>

      <Footer />
    </Box>
  );
};

export default AssetsPage;
